import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { ArrowLeft, ArrowRight, Maximize2, Play, RotateCcw, X } from "lucide-react";
import { findSceneNode, getSceneNodeChildren } from "@/document/model/document";
import type { CurvaDocument } from "@/document/model/document";
import type { FrameNode, SceneNode } from "@/document/model/nodes";
import { outgoingPrototypeLink } from "@/document/model/prototype";
import { documentStore } from "@/editor/store";
import { useStoreSubscribe } from "@/lib/store";
import { exportSceneNodeToPngBlob } from "@/export/pngExporter";

interface PrototypePlayerModalProps {
  startFrameId: string;
  onClose: () => void;
}

export function PrototypePlayerModal({ startFrameId, onClose }: PrototypePlayerModalProps) {
  const document = useStoreSubscribe(documentStore.document);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [history, setHistory] = useState([startFrameId]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [imageUrl, setImageUrl] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [blinkToken, setBlinkToken] = useState(0);
  const currentFrameId = history[historyIndex] ?? startFrameId;
  const frame = findSceneNode(document, currentFrameId);
  const currentFrame = frame?.type === "frame" ? frame : undefined;
  const clickableItems = useMemo(
    () => currentFrame ? clickablePrototypeItems(document, currentFrame) : [],
    [document, currentFrame?.id],
  );

  useEffect(() => {
    setHistory([startFrameId]);
    setHistoryIndex(0);
  }, [startFrameId]);

  useEffect(() => {
    let cancelled = false;
    let nextUrl: string | undefined;
    setLoading(true);
    setImageUrl(current => {
      if (current) {
        URL.revokeObjectURL(current);
      }
      return undefined;
    });

    void (async () => {
      if (!currentFrame) {
        setLoading(false);
        return;
      }
      const blob = await exportSceneNodeToPngBlob(document, currentFrame, prototypeRasterScale());
      if (cancelled) {
        return;
      }
      nextUrl = URL.createObjectURL(blob);
      setImageUrl(nextUrl);
      setLoading(false);
    })().catch(() => {
      if (!cancelled) {
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      if (nextUrl) {
        URL.revokeObjectURL(nextUrl);
      }
    };
  }, [document, currentFrame?.id]);

  useEffect(() => {
    if (blinkToken === 0) {
      return;
    }
    const timeout = window.setTimeout(() => setBlinkToken(0), 700);
    return () => window.clearTimeout(timeout);
  }, [blinkToken]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      event.stopPropagation();
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === "ArrowLeft" || event.key === "Backspace") {
        event.preventDefault();
        setHistoryIndex(index => Math.max(0, index - 1));
        return;
      }
      if (event.key === "ArrowRight" || event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        setHistoryIndex(index => Math.min(history.length - 1, index + 1));
        return;
      }
      if (event.key.toLowerCase() === "r" && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        resetToStart();
      }
    };

    window.addEventListener("keydown", handleKeyDown, true);
    return () => window.removeEventListener("keydown", handleKeyDown, true);
  }, [history.length, onClose, startFrameId]);

  function navigateTo(frameId: string): void {
    setHistory(current => [...current.slice(0, historyIndex + 1), frameId]);
    setHistoryIndex(index => index + 1);
    setBlinkToken(0);
  }

  function resetToStart(): void {
    setHistory([startFrameId]);
    setHistoryIndex(0);
    setBlinkToken(0);
  }

  function requestFullscreen(): void {
    const root = rootRef.current;
    if (!root) {
      return;
    }
    if (globalThis.document.fullscreenElement) {
      void globalThis.document.exitFullscreen();
      return;
    }
    void root.requestFullscreen();
  }

  const aspectRatio = currentFrame
    ? `${Math.max(1, Math.abs(currentFrame.bounds.width))} / ${Math.max(1, Math.abs(currentFrame.bounds.height))}`
    : "1 / 1";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-6" role="dialog" aria-modal="true">
      <div ref={rootRef} className="flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded border border-white/10 bg-[#15191f] shadow-2xl">
        <div className="flex h-11 shrink-0 items-center gap-1 border-b border-white/10 px-2 text-slate-300">
          <button type="button" aria-label="Back" title="Back" disabled={historyIndex <= 0} onClick={() => setHistoryIndex(index => Math.max(0, index - 1))} className="grid size-8 place-items-center rounded text-slate-400 hover:bg-white/10 hover:text-slate-100 disabled:pointer-events-none disabled:opacity-35">
            <ArrowLeft className="size-4" aria-hidden="true" />
          </button>
          <button type="button" aria-label="Forward" title="Forward" disabled={historyIndex >= history.length - 1} onClick={() => setHistoryIndex(index => Math.min(history.length - 1, index + 1))} className="grid size-8 place-items-center rounded text-slate-400 hover:bg-white/10 hover:text-slate-100 disabled:pointer-events-none disabled:opacity-35">
            <ArrowRight className="size-4" aria-hidden="true" />
          </button>
          <button type="button" aria-label="Reset" title="Reset" onClick={resetToStart} className="grid size-8 place-items-center rounded text-slate-400 hover:bg-white/10 hover:text-slate-100">
            <RotateCcw className="size-4" aria-hidden="true" />
          </button>
          <div className="min-w-0 flex-1 truncate px-2 text-[12px] font-medium text-slate-100">{currentFrame?.name ?? "Prototype"}</div>
          <button type="button" aria-label="Fullscreen" title="Fullscreen" onClick={requestFullscreen} className="grid size-8 place-items-center rounded text-slate-400 hover:bg-white/10 hover:text-slate-100">
            <Maximize2 className="size-4" aria-hidden="true" />
          </button>
          <button type="button" aria-label="Close" title="Close" onClick={onClose} className="grid size-8 place-items-center rounded text-slate-400 hover:bg-white/10 hover:text-slate-100">
            <X className="size-4" aria-hidden="true" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-[#0f1216] p-6">
          <div
            className="relative mx-auto max-h-full max-w-full overflow-hidden bg-white"
            style={{ aspectRatio }}
            onClick={() => setBlinkToken(token => token + 1)}
          >
            {imageUrl ? (
              <img src={imageUrl} alt={currentFrame?.name ?? "Prototype frame"} className="block h-full w-full select-none object-contain" draggable={false} />
            ) : (
              <div className="grid h-full min-h-72 place-items-center text-[12px] text-slate-500">
                {loading ? "Rendering" : "Frame unavailable"}
              </div>
            )}
            {currentFrame ? clickableItems.map(item => (
              <button
                key={`${item.node.id}-${item.link.targetFrameId}`}
                type="button"
                aria-label={`Open ${item.node.name}`}
                title={item.node.name}
                onClick={event => {
                  event.stopPropagation();
                  navigateTo(item.link.targetFrameId);
                }}
                className={`absolute rounded-sm transition-shadow ${blinkToken > 0 ? "ring-2 ring-sky-400 ring-offset-2 ring-offset-sky-400/20" : ""}`}
                style={relativeButtonStyle(currentFrame, item.node)}
              >
                <span className="sr-only">
                  <Play className="size-3" aria-hidden="true" />
                </span>
              </button>
            )) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function clickablePrototypeItems(document: CurvaDocument, frame: FrameNode): Array<{ node: SceneNode; link: NonNullable<ReturnType<typeof outgoingPrototypeLink>> }> {
  return descendantNodes(frame).flatMap(node => {
    const link = outgoingPrototypeLink(document, node.id);
    const target = link ? findSceneNode(document, link.targetFrameId) : undefined;
    return link && target?.type === "frame" ? [{ node, link }] : [];
  });
}

function descendantNodes(node: SceneNode): SceneNode[] {
  const children = getSceneNodeChildren(node);
  if (!children) {
    return [];
  }
  return children.flatMap(child => [child, ...descendantNodes(child)]);
}

function relativeButtonStyle(frame: FrameNode, node: SceneNode): CSSProperties {
  const frameWidth = Math.max(1, Math.abs(frame.bounds.width));
  const frameHeight = Math.max(1, Math.abs(frame.bounds.height));
  const x = Math.min(node.bounds.x, node.bounds.x + node.bounds.width);
  const y = Math.min(node.bounds.y, node.bounds.y + node.bounds.height);
  const width = Math.max(8, Math.abs(node.bounds.width));
  const height = Math.max(8, Math.abs(node.bounds.height));
  return {
    left: `${((x - frame.bounds.x) / frameWidth) * 100}%`,
    top: `${((y - frame.bounds.y) / frameHeight) * 100}%`,
    width: `${(width / frameWidth) * 100}%`,
    height: `${(height / frameHeight) * 100}%`,
  };
}

function prototypeRasterScale(): number {
  return Math.max(2, Math.min(4, (globalThis.devicePixelRatio || 1) * 2));
}
